/**
 * LocationsSearchList
 *
 * Areas shown here come ONLY from the real paginated endpoint:
 * GET /api/v3/facilities/{facilityId}/locations?page=1&limit=10 (via useLocationsInfinite).
 * No other data source (e.g. full-list or mock) is used for the table.
 *
 * Same search UX as FacilitiesSearchList (senior flow):
 * - Show "Searching areas..." as soon as user types; hide list until backend returns.
 * - Debounce + AbortSignal so previous request is cancelled if user keeps typing.
 * - Backend-driven search (endpoint receives search param).
 */

import React, { useEffect, useMemo, useRef, memo } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { LocationRow } from './LocationRow';
import { useLocationsInfinite } from '../../hooks/useLocationsInfinite';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

const SEARCH_DEBOUNCE_MS = 350;

/** Memoized header: search input + Clear. Controlled by parent. */
const LocationsSearchHeader = memo(function LocationsSearchHeader({ searchValue, onSearchChange }) {
  return (
    <div className="flex items-center gap-3">
      <div className="relative flex items-center flex-1">
        <div className="bg-white border border-[#d3d6dc] rounded-[10px] h-[37px] pl-[7px] pr-7 flex items-center gap-[10px] w-full">
          <Search className="w-6 h-6 text-[#6e6e71] shrink-0" />
          <input
            type="text"
            placeholder="Search areas..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="font-['Roboto:Regular',sans-serif] font-normal text-[18px] text-[#6e6e71] bg-transparent border-none outline-none w-full placeholder:text-[#6e6e71]"
            style={{ fontVariationSettings: "'wdth' 100" }}
            aria-label="Search areas"
          />
        </div>
      </div>
      {searchValue && (
        <button
          type="button"
          onClick={() => onSearchChange('')}
          className="text-sm text-[#61b8e7] hover:text-[#4da3d1] font-['Roboto:Medium',sans-serif]"
        >
          Clear
        </button>
      )}
    </div>
  );
});

export const LocationsSearchList = ({
  selectedFacilityId,
  queryScope,
  selectedLocationIds,
  onSelectLocation,
  onSelectAll,
  onUpdateLocation,
  onDuplicateLocation,
  onOpenStandards,
  showCleaningStandards,
  readOnlyMode,
  locationsWithStandards,
  cleaningStandardsStatusByLocationId,
  onLocationsChange,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  // Single source of truth: real paginated API only (getLocationsByFacilityPaginated)
  const {
    locations,
    totalCount,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useLocationsInfinite({
    facilityId: selectedFacilityId || '',
    queryScope,
    limit: 10,
    search: debouncedSearch ?? '',
    enabled: !!selectedFacilityId,
  });

  const searchTrimmed = (searchQuery && searchQuery.trim()) || '';
  const debouncedTrimmed = (debouncedSearch && String(debouncedSearch).trim()) || '';

  // Same UX as facilities: show "Searching..." as soon as user types; don't show list until we have results
  const isDebouncing = searchTrimmed !== debouncedTrimmed;
  const isWaitingForBackend = isFetching && locations.length === 0;
  const isSearchActive = searchTrimmed.length > 0;
  const isSearching = isSearchActive && (isDebouncing || isWaitingForBackend);

  const displayedLocations = useMemo(() => {
    if (isSearching) return [];
    return locations;
  }, [locations, isSearching]);

  // Only notify parent when the list of location ids actually changed (avoids re-render loop)
  const locationIdsKey = useMemo(
    () => displayedLocations.map((l) => l.id).join(','),
    [displayedLocations],
  );
  const prevLocationIdsRef = useRef('');
  useEffect(() => {
    if (locationIdsKey !== prevLocationIdsRef.current) {
      prevLocationIdsRef.current = locationIdsKey;
      onLocationsChange?.(displayedLocations);
    }
  }, [locationIdsKey, displayedLocations, onLocationsChange]);

  const isInitialLoad = isLoading && locations.length === 0;
  const isEmptyAfterLoad = !isLoading && locations.length === 0 && !isSearching;

  const isAllDisplayedSelected =
    displayedLocations.length > 0 &&
    displayedLocations.every((loc) => selectedLocationIds.includes(loc.id));
  const showSelectAll = displayedLocations.length > 0 && onSelectAll;

  return (
    <div className="flex flex-col gap-6 h-full">
      {/* Search */}
      <LocationsSearchHeader searchValue={searchQuery} onSearchChange={setSearchQuery} />

      {/* Same UX as facilities: Searching card as soon as user types; list only when we have results */}
      {isSearching ? (
        <div
          className="flex flex-col items-center justify-center rounded-xl bg-[#f8fbfd] border border-[#e5eaf0] min-h-[200px] px-6 py-10"
          role="status"
          aria-live="polite"
          aria-label="Searching areas"
        >
          <Loader2 className="w-8 h-8 text-[#61b8e7] animate-spin mb-4" aria-hidden />
          <p className="font-['Roboto:Regular',sans-serif] text-[15px] text-[#6e6e71] text-center">
            Searching areas...
          </p>
          <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-[#9e9e9e] text-center mt-1">
            {isDebouncing ? 'Waiting for you to finish typing...' : 'Fetching results...'}
          </p>
        </div>
      ) : isInitialLoad ? (
        <div
          className="flex flex-col items-center justify-center rounded-xl bg-[#f8fbfd] border border-[#e5eaf0] min-h-[280px] px-6 py-10"
          role="status"
          aria-live="polite"
          aria-label="Loading areas"
        >
          <Loader2 className="w-8 h-8 text-[#61b8e7] animate-spin mb-4" aria-hidden />
          <p className="font-['Roboto:Regular',sans-serif] text-[15px] text-[#6e6e71] text-center">
            Loading areas...
          </p>
          <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-[#9e9e9e] text-center mt-1">
            This may take a moment
          </p>
        </div>
      ) : isEmptyAfterLoad ? (
        <div
          className="py-8 text-center rounded-xl bg-[#f8fbfd] border border-[#e5eaf0] px-6"
          role="status"
        >
          <span className="font-['Roboto:Regular',sans-serif] text-sm text-[#6e6e71]">
            {isSearchActive
              ? 'No areas match your search. Try a different term or clear the search.'
              : 'No areas yet. Add your first area below.'}
          </span>
        </div>
      ) : (
      <>
      {/* Table header: Select All checkbox (first cell) + column labels */}
        <div
            className={`grid gap-4 px-4 pb-2 border-b border-gray-200 items-center ${
              showCleaningStandards
                ? 'grid-cols-[auto_1fr_160px_100px]'
                : 'grid-cols-[auto_1fr_100px]'
            }`}
          >
            {/* Select All in header - same celeste checkbox as row checkboxes */}
            {showSelectAll ? (
              <button
                type="button"
                onClick={() => onSelectAll(displayedLocations.map((loc) => loc.id))}
                className="flex items-center justify-center w-[18px] h-[18px] hover:opacity-80 transition-opacity group"
                aria-label="Select all areas"
              >
                <div className="relative shrink-0">
                  <input
                    type="checkbox"
                    checked={isAllDisplayedSelected}
                    readOnly
                    className="size-[18px] appearance-none bg-white border-2 border-[#9e9e9e] rounded-[3px] cursor-pointer checked:bg-[#61b8e7] checked:border-[#61b8e7] transition-all group-hover:border-[#61b8e7] pointer-events-none"
                    tabIndex={-1}
                  />
                  {isAllDisplayedSelected && (
                    <svg
                      className="absolute left-[3px] top-[3px] w-[12px] h-[12px] pointer-events-none"
                      fill="none"
                      viewBox="0 0 12 10"
                    >
                      <path
                        d="M1.5 5L4.5 8L10.5 1.5"
                        stroke="white"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </button>
            ) : (
              <div className="w-[18px]" aria-hidden />
            )}
            <p
              className="font-['Roboto:Medium',sans-serif] font-medium text-[14px] text-[#6e6e71]"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              Area Name
            </p>
            {showCleaningStandards && (
              <p
                className="font-['Roboto:Medium',sans-serif] font-medium text-[14px] text-[#6e6e71]"
                style={{ fontVariationSettings: "'wdth' 100" }}
              >
                Cleaning Standards
              </p>
            )}
            <p
              className="font-['Roboto:Medium',sans-serif] font-medium text-[14px] text-[#6e6e71] text-center"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              Actions
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {displayedLocations.map((location) => (
                <LocationRow
                  key={location.id}
                  location={location}
                  isSelected={selectedLocationIds.includes(location.id)}
                  onSelect={onSelectLocation}
                  onUpdate={onUpdateLocation}
                  onDuplicate={onDuplicateLocation}
                  onOpenStandards={onOpenStandards}
                  hasAnySelection={selectedLocationIds.length > 0}
                  readOnlyMode={readOnlyMode}
                  hasStandards={locationsWithStandards.has(location.id)}
                  cleaningStandardsStatus={
                    cleaningStandardsStatusByLocationId[location.id] ??
                    (location.cleaningStandardsStatus === 'assigned'
                      ? 'set'
                      : location.cleaningStandardsStatus)
                  }
                  showCleaningStandards={showCleaningStandards}
                />
              ))}
            </div>

            {hasNextPage && (
              <div className="pt-2 flex flex-col items-center justify-center gap-3">
                {isFetchingNextPage ? (
                  <div className="flex items-center gap-3 text-[#61b8e7] py-4">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-['Roboto:Regular',sans-serif] text-sm">
                      Loading more areas...
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fetchNextPage()}
                    className="w-full px-6 py-3 border-2 border-[#61b8e7] rounded-lg text-[#61b8e7] hover:bg-[#61b8e7] hover:text-white transition-all font-['Roboto:Medium',sans-serif] text-sm shadow-sm hover:shadow-md"
                  >
                    Load More
                  </button>
                )}
              </div>
            )}

            <div className="py-2 border-t border-gray-100 text-center">
              <span className="font-['Roboto:Regular',sans-serif] text-xs text-gray-500">
                {`Showing ${displayedLocations.length} of ${isSearchActive ? displayedLocations.length : totalCount} areas`}
              </span>
            </div>
          </div>
      </>
      )}
    </div>
  );
};
